import React, { useCallback, useState } from 'react';
import { Empty, Modal } from 'antd';
import { useQueryClient } from 'react-query';
import moment from 'moment';
import InnerCard from '../../../../../components/InnerCard/InnerCard';
import Tenant from './Tenant';
import { CardChangeButton } from '../../../../../../../components/Card';
import useChangeModalVisible from '../../../../../../../hooks/useChangeModalVisible';
import AddTenantToRoomModal from '../../../../../components/modals/AddTenantToRoomModal/AddTenantToRoomModal';
import RemoveTenantFromRoomModal from '../../../../../components/modals/RemoveTenantFromRoomModal/RemoveTenantFromRoomModal';
import DeleteOrEndRoomTenant from '../../../../../components/modals/DeleteOrEndRoomTenantModal/DeleteOrEndRoomTenantModal';
import getName from '../../../../../../../helpers/getName';
import useDeleteRoomTenantMutation from '../../../../../mutations/useDeleteRoomTenantMutation';
import { roomQueryKeys } from '../../../../../queries/queryKeys';
import commonStyles from '../../../AboutRoom.module.scss';

const { confirm } = Modal;

const currentUtcDate = moment().utc().format();

const isAfterCurrent = (date) => moment(date).isAfter(currentUtcDate, 'day');
const isCurrentBetween = (startDate, endDate) =>
  moment(currentUtcDate).isBetween(startDate, endDate, 'day', '[]');

const TenantBlock = ({ room = {} }) => {
  const [currentTenant, setCurrentTenant] = useState(null);

  const queryClient = useQueryClient();

  const {
    mutateAsync: deleteTenantMutateAsync,
    isLoading: isDeleteTenantLoading,
  } = useDeleteRoomTenantMutation({
    onSuccess: () => queryClient.invalidateQueries(roomQueryKeys(room.id)),
  });

  const {
    visible: addEndEditModalVisible,
    showModal: showAddAndEditModal,
    hideModal: hideAddAndEditModal,
  } = useChangeModalVisible();

  const {
    visible: endTenantModalVisible,
    showModal: showEndTenantModal,
    hideModal: hideEndTenantModal,
  } = useChangeModalVisible();

  const {
    visible: choiceModalVisible,
    showModal: showChoiceModal,
    hideModal: hideChoiceModal,
  } = useChangeModalVisible();

  const handleCancel = useCallback(() => {
    if (Boolean(currentTenant)) {
      setCurrentTenant(null);
    }

    if (addEndEditModalVisible) {
      hideAddAndEditModal();
    } else if (endTenantModalVisible) {
      hideEndTenantModal();
    } else {
      hideChoiceModal();
    }
  }, [currentTenant, addEndEditModalVisible, endTenantModalVisible]);

  const handleEditBtnClick = useCallback((tenant) => {
    setCurrentTenant(tenant);
    showAddAndEditModal();
  }, []);

  const handleRemoveBtnClick = useCallback((tenant) => {
    setCurrentTenant(tenant);

    tenant.canBeDeleted ? showChoiceModal() : showEndTenantModal();
  }, []);

  const handleDeleteRoomTenant = () => {
    confirm({
      title: `Вы уверены, что хотите удалить арендатора ${
        currentTenant.name ? currentTenant.name : getName(currentTenant)
      }?`,
      centered: true,
      okText: 'Да',
      okButtonProps: { loading: isDeleteTenantLoading },
      cancelButtonProps: { disabled: isDeleteTenantLoading },
      onOk: async () => {
        await deleteTenantMutateAsync({
          roomId: room.id,
          tenantId: currentTenant.id,
        });

        handleCancel();
      },
    });
  };

  const handleEndRoomTenant = () => {
    showEndTenantModal();
    hideChoiceModal();
  };

  const showAddTenantBtn =
    !room?.tenants?.length ||
    (Boolean(room?.tenants?.length) &&
      room.tenants.every((tenant) => Boolean(tenant.accountEndDate)));

  return (
    <>
      <InnerCard
        title="Арендатор"
        action={
          showAddTenantBtn ? (
            <CardChangeButton
              text="Добавить"
              className={commonStyles.innerCardAction}
              onClick={showAddAndEditModal}
            />
          ) : undefined
        }
      >
        {Boolean(room?.tenants?.length) ? (
          room.tenants.map((tenant) => {
            const { accountStartDate: start, accountEndDate: end } = tenant;

            const currentBetween = isCurrentBetween(start, end);
            const startAfterCurrent = isAfterCurrent(start);

            if (currentBetween || startAfterCurrent) {
              return (
                <Tenant
                  key={tenant.id}
                  tenant={tenant}
                  onEditBtnClick={
                    !tenant.accountEndDate ? handleEditBtnClick : undefined
                  }
                  onRemoveBtnClick={
                    !tenant.accountEndDate ? handleRemoveBtnClick : undefined
                  }
                />
              );
            }

            return null;
          })
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </InnerCard>
      <AddTenantToRoomModal
        visible={addEndEditModalVisible}
        onCancel={handleCancel}
        initialValues={currentTenant}
        roomId={room.id}
      />
      <RemoveTenantFromRoomModal
        visible={endTenantModalVisible}
        onCancel={handleCancel}
        initialValues={currentTenant}
        roomId={room.id}
      />
      <DeleteOrEndRoomTenant
        visible={choiceModalVisible}
        onCancel={handleCancel}
        onDelete={handleDeleteRoomTenant}
        onEnd={handleEndRoomTenant}
      />
    </>
  );
};

export default TenantBlock;
